import React from "react";
import { Contact } from "lucide-react";
import { Card, Input, Select, TagGroup, Toggle, MultiSelect, TimePicker, PillGroup } from "./FormComponents";

export const Step3 = ({ data, updateData, isLight, showValidation }) => {
  const handleNestedChange = (field, value) => {
    updateData("contactDetails", {
      ...data,
      [field]: value
    });
  };

  const contactTypes = [
    "Host / Owner",
    "Reception / Front Desk",
    "Property Manager / Management Company"
  ];

  const messagingApps = ["Booking Platform", "WhatsApp", "Viber", "Other"];
  
  const languages = [
    "Arabic", "Bengali", "Bulgarian", "Chinese", "Croatian", "Czech", "Danish", 
    "Dutch", "English", "Filipino", "Finnish", "French", "German", "Greek", 
    "Gujarati", "Hebrew", "Hindi", "Hungarian", "Indonesian", "Italian", 
    "Japanese", "Kannada", "Korean", "Malay", "Malayalam", "Marathi", 
    "Norwegian", "Odia", "Polish", "Portuguese", "Punjabi", "Romanian", 
    "Russian", "Serbian", "Slovak", "Slovenian", "Spanish", "Swedish", 
    "Tamil", "Telugu", "Thai", "Turkish", "Urdu", "Vietnamese"
  ].sort();

  return (
    <div className="flex flex-col gap-8">
      <Card title="Contact Details" icon={Contact} subtitle="Who guests can reach out to during their stay." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Select 
                label="Who is the main guest contact for this property?" 
                options={contactTypes}
                value={data.mainContactType}
                onChange={(e) => handleNestedChange("mainContactType", e.target.value)}
                required
                isLight={isLight}
                showValidation={showValidation}
              />
            </div>

            <Input 
              label="Contact Name" 
              placeholder="John Doe" 
              value={data.name}
              onChange={(e) => handleNestedChange("name", e.target.value)}
              required
              isLight={isLight}
              showValidation={showValidation}
            />
            <Input 
              label="Phone" 
              placeholder="+30 697..." 
              value={data.phone}
              onChange={(e) => handleNestedChange("phone", e.target.value.replace(/[^0-9+]/g, ""))}
              required
              isLight={isLight}
              showValidation={showValidation}
            />
            <Input 
              label="Email" 
              type="email"
              placeholder="contact@property.com" 
              value={data.email}
              onChange={(e) => handleNestedChange("email", e.target.value)}
              required
              isLight={isLight}
              showValidation={showValidation}
              error={data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) ? "Invalid email format" : null}
            />
            <div className="flex flex-col gap-4">
              <PillGroup 
                label="Contact availability hours" 
                options={["Anytime", "Specific time"]}
                value={data.availabilityHours === "Anytime" ? "Anytime" : "Specific time"}
                onChange={(val) => {
                  if (val === "Anytime") {
                    handleNestedChange("availabilityHours", "Anytime");
                  } else {
                    handleNestedChange("availabilityHours", "09:00 - 18:00");
                  }
                }}
                required
                isLight={isLight}
                showValidation={showValidation}
              />
              {data.availabilityHours !== "Anytime" && (
                <div className="flex items-center gap-2">
                  <TimePicker 
                    label="From" 
                    value={data.availabilityHours.split(" - ")[0] || "09:00"}
                    onChange={(val) => {
                      const until = data.availabilityHours.split(" - ")[1] || "18:00";
                      handleNestedChange("availabilityHours", `${val} - ${until}`);
                    }}
                    required
                    isLight={isLight}
                    showValidation={showValidation}
                  />
                  <TimePicker 
                    label="Until" 
                    value={data.availabilityHours.split(" - ")[1] || "18:00"}
                    onChange={(val) => {
                      const from = data.availabilityHours.split(" - ")[0] || "09:00";
                      handleNestedChange("availabilityHours", `${from} - ${val}`);
                    }}
                    required
                    isLight={isLight}
                    showValidation={showValidation}
                  />
                </div>
              )}
            </div>
          </div>

          <Toggle 
            label="Are any messaging apps available for this contact?" 
            value={data.messagingApps.available}
            onChange={(val) => {
              const newMessaging = { 
                ...data.messagingApps, 
                available: val,
                apps: val ? data.messagingApps.apps : [],
                otherApp: val ? data.messagingApps.otherApp : ""
              };
              handleNestedChange("messagingApps", newMessaging);
            }}
            isLight={isLight}
          >
            <div className="flex flex-col gap-6">
              <TagGroup 
                label="Available messaging apps" 
                options={messagingApps}
                value={data.messagingApps.apps}
                onChange={(val) => {
                  const newMessaging = { 
                    ...data.messagingApps, 
                    apps: val,
                    otherApp: val.includes("Other") ? data.messagingApps.otherApp : ""
                  };
                  handleNestedChange("messagingApps", newMessaging);
                }}
                isLight={isLight}
              />
              {data.messagingApps.apps.includes("Other") && (
                <Input 
                  label="Specify other messaging app" 
                  placeholder="Telegram, WeChat" 
                  value={data.messagingApps.otherApp}
                  onChange={(e) => {
                    const newMessaging = { ...data.messagingApps, otherApp: e.target.value };
                    handleNestedChange("messagingApps", newMessaging);
                  }}
                  required
                  isLight={isLight}
                />
              )}
            </div>
          </Toggle>

          <div className="flex flex-col gap-6">
            <MultiSelect 
              label="Languages spoken" 
              options={languages}
              value={data.languages}
              onChange={(val) => handleNestedChange("languages", val)}
              required
              isLight={isLight}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
